import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../../../lib/api';

/** Types of the customer flow (docs/dev/api-reservations.md „Customer flow"). */

export interface PublicReservation {
  state: 'open' | 'signed' | 'aborted';
  unit: {
    number: string;
    propertyName: string;
    address: string | null;
    building: string | null;
    floor: string | null;
    areaSqm: number | null;
    rooms: number | null;
    priceCents: number | null;
    parkingSpace: { number: string; priceCents: number | null } | null;
    serviceChargeCents: number | null;
  };
  buyer: { name: string; address: string | null; email: string | null };
  seller: string | null;
  advisor: { name: string; email: string; phone: string | null };
  organization: { name: string; imprintUrl: string | null; privacyUrl: string | null };
  agreementText: string;
  agreementPages: number;
  agreementPdfUrl: string;
  openedAt: string | null;
  signedAt: string | null;
  approvalRequired: boolean;
  abortReason: string | null;
  abortReasons: string[];
  linkValidUntil: string;
}

/** `expired`, or the status of an ended reservation (`withdrawn` · `released` · `rejected`). */
export type PublicLinkState = string;

export interface PublicLinkGone {
  message: string;
  state: PublicLinkState;
  advisor: { name: string; email: string | null; phone: string | null } | null;
}

export const publicReservationKey = (token: string) => ['public-reservation', token] as const;

const base = (token: string) => `/public/reservations/${encodeURIComponent(token)}`;

export function usePublicReservation(token: string) {
  return useQuery({
    queryKey: publicReservationKey(token),
    queryFn: ({ signal }) => api<PublicReservation>(base(token), { signal }),
    retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 1,
    staleTime: 30_000,
  });
}

/** The 410 of the customer flow (expired or no longer valid) with the advisor to ask, or null. */
export function goneOf(error: unknown): PublicLinkGone | null {
  if (!(error instanceof ApiError) || error.status !== 410) return null;
  const advisor = error.details?.['advisor'];
  return {
    message: error.message,
    state: error.detailString('state') ?? 'expired',
    advisor:
      typeof advisor === 'object' && advisor !== null
        ? (advisor as { name: string; email: string | null; phone: string | null })
        : null,
  };
}

export function isNotFound(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

export function usePublicReservationMutations(token: string) {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: publicReservationKey(token) });
  const sign = useMutation({
    mutationFn: (body: { signaturePng: string; place: string; consent: boolean }) =>
      api<{ state: 'signed'; approvalRequired: boolean; signedAt: string }>(`${base(token)}/sign`, {
        method: 'POST',
        body,
      }),
    onSuccess: refresh,
  });
  const abort = useMutation({
    mutationFn: (reason: string | null) =>
      api<{ state: 'aborted' }>(`${base(token)}/abort`, {
        method: 'POST',
        body: reason === null ? {} : { reason },
      }),
    onSuccess: refresh,
  });
  const resume = useMutation({
    mutationFn: () => api<{ state: 'open' }>(`${base(token)}/continue`, { method: 'POST', body: {} }),
    onSuccess: refresh,
  });
  return { sign, abort, resume };
}
